import React, { useEffect, useRef, useState } from "react";
import { ModalHeader } from "./modal/ModalHeader";
import { EditNameInput } from "./edit/EditNameInput";
import { EditFooter } from "./modal/footers/EditFooter";
import { SavedSearchModal } from "./modal/SavedSearchModal";
import { closableErrorToast, shortLivedSuccessToast } from "../utils";
import { Category, BaseSavedSearch } from "../model/model";
import { useSavedSearchService } from "../services/useSavedSearchService";
import { SavedSearchValidator } from "../validators/SavedSearchValidator";
import { useAppTranslation } from "../locales/translate";

export interface Props {
  isOpen: boolean;
  closeModal: () => void;
  search: BaseSavedSearch;
  category: Category;
  onCancel?: () => void;
  onSave?: (savedSearch: BaseSavedSearch) => void;
}

export const EditNameSavedSearchModal = ({
  isOpen,
  closeModal,
  search,
  category,
  onCancel = closeModal,
  onSave,
}: Props) => {
  const savedSearchService = useSavedSearchService(category);
  const focusRef = useRef(null);
  const [name, setName] = useState(search.name);
  const [isNameValid, setIsNameValid] = useState(true);
  const [isEditDisabled, setIsEditDisabled] = useState(false);

  const { t } = useAppTranslation();

  useEffect(() => {
    setName(search.name);
  }, [search.name]);

  const onModalClose = () => {
    setName(search.name);
    closeModal();
  };

  const onModalCancel = () => {
    setName(search.name);
    closeModal();
    onCancel();
  };

  const editSavedSearchName = () => {
    setIsEditDisabled(true);

    savedSearchService
      .updateSavedSearchName(search, name)
      .then((updatedSearch) => {
        if (onSave) onSave(updatedSearch);
        shortLivedSuccessToast(t("TOAST_SEARCH_UPDATE_SUCCESS"));
      })
      .then(closeModal)
      .catch(() => closableErrorToast(t("TOAST_SEARCH_RENAME_ERROR")))
      .finally(() => setIsEditDisabled(false));
  };

  const handleNameChange = (changedName: string) => {
    if (SavedSearchValidator.isNameValid(changedName)) {
      setIsNameValid(true);
    }
    setName(changedName);
  };

  const onEditConfirm = () => {
    if (SavedSearchValidator.isNameValid(name)) {
      editSavedSearchName();
    } else {
      setIsNameValid(false);
    }
  };

  return (
    <SavedSearchModal
      narrow
      isOpen={isOpen}
      onDismiss={onModalClose}
      focusRef={focusRef}
      body={
        <>
          <ModalHeader
            iconName="bell"
            title={search.name}
            focusRef={focusRef}
          />
          <EditNameInput
            label={t("RENAME_MODAL_NAME_INPUT_LABEL")}
            name={name}
            editName={handleNameChange}
            isInvalid={!isNameValid}
            onEnter={onEditConfirm}
          />
        </>
      }
      footer={
        <EditFooter
          onConfirm={onEditConfirm}
          onCancel={onModalCancel}
          editDisabled={isEditDisabled}
        />
      }
    />
  );
};
